import * as _ from 'lodash'
import {DimensionType, IHierarchy, IHierarchyLevel, IHierarchyNode} from "../PA.Core/PA.Core.IF.Hierarchy";
import {
    WB_ProductGroupRelationshipRow,
    WB_ProductGroupRow,
    WB_FinishedProductRelationshipRow,
    WB_FinishedProductRow,
    WB_ProductHierarchyRow
} from "../PA.Core/PAdbSchemaRows";
import {MessageProvider} from "../PA.Core/Basic/Messages";
import {IMessageProvider, MessageType} from "../PA.Core/PA.Core.IF.Basic";
import {PAdbContext} from "../PA.Core/PAdbContext";
import {
    esWB_Finished_Product_Relationship,
    esWB_Period_Hierarchy_Level,
    esWB_Product_Group_Relationship,
    esWB_Product_Hierarchy
} from "../PA.Core/PAdbSchema";
import {ESRecordResolver} from "../PA.Core/lib/ESRecordResolver";
import {DateUtils} from "../PA.Core/Basic/Dates";
import {HierarchyBuilderService} from "./HierarchyBuilderService";


export interface CreateOptions {
    bExcludeProducts?: boolean,
    bSortById?: boolean,
    bUseLegacyLevel?: boolean
}

export class ProductHierarchy
    implements IHierarchy {

    private _wbPrdHId: number
    private _PrdHId: string
    private _PrdHName: string
    private _effectiveStartDt: Date
    private _effectiveEndDt: Date
    private dbContext: PAdbContext
    private bSortById: boolean

    private dctHierarchyLevels: { [LevelId: string]: IHierarchyLevel }
    private dctNodes: { [NodeId: string]: IHierarchyNode }
    private dctNodesByLevel: { [LevelId: string]: IHierarchyNode[] }

    private dctNodeParentByChildId: { [NodeId: string]: IHierarchyNode }
    private dctNodeChildrenByParentId: { [NodeId: string]: IHierarchyNode[] }
    private arrTopLevelNodes: IHierarchyNode[]

    private _loadMessageProvider: MessageProvider;
    private _OnLoadComplete: Promise<void>
    private _bLoaded: boolean
    private _bUseHierarchyBuilder: boolean
    private _bExcludeProducts: boolean
    private _bUseLegacyLevel: boolean

    constructor(wbPrdHId: number, EffectiveStartDate: Date, EffectiveEndDate: Date, options?: CreateOptions) {
        this.dbContext = new PAdbContext()
        this._wbPrdHId = wbPrdHId;
        this._PrdHId = "Not Constructed"
        this._PrdHName = "Not Constructed"
        this._effectiveStartDt = EffectiveStartDate
        this._effectiveEndDt = EffectiveEndDate
        this._bLoaded = false
        this._loadMessageProvider = new MessageProvider()
        this.dctHierarchyLevels = {}
        this.dctNodes = {}
        this.dctNodesByLevel = {}
        this.dctNodeParentByChildId = {}
        this.dctNodeChildrenByParentId = {}
        this.arrTopLevelNodes = []
        this._bExcludeProducts = true
        const opts = options || {}
        if ('bExcludeProducts' in opts)
            this._bExcludeProducts = options.bExcludeProducts
        if(_.isBoolean(opts.bUseLegacyLevel))
            this._bUseLegacyLevel = opts.bUseLegacyLevel
        else
            this._bUseLegacyLevel = false

        this.bSortById = !!opts.bSortById
        this._bUseHierarchyBuilder = true
    }


    get DimensionType(): DimensionType {
        return DimensionType.Product;
    }

    get HierarchyId(): string {
        return this._wbPrdHId + ''
    }

    get HierarchyIdUI(): string {
        return this._PrdHId
    }

    get HierarchyName(): string {
        return this._PrdHName
    }

    get bHasSortOrder(): boolean {
        return true
    }

    get LoadMessageProvider(): IMessageProvider {
        return this._loadMessageProvider
    }

    get bLoaded(): boolean {
        return this._bLoaded
    }


    get OnLoadComplete(): Promise<void> {
        const self = this

        if (self._bLoaded)
            return Promise.resolve()


        if (!_.isEmpty(self._OnLoadComplete))
            return self._OnLoadComplete

        let returnValue: Promise<void>

        if (self._bUseHierarchyBuilder)
            returnValue = self._OnLoadComplete = self.OnLoadComplete_UseHierarchyBuilder
        else
            returnValue = self._OnLoadComplete = self.OnLoadComplete_UseJSData

        return returnValue
    }

    private get OnLoadComplete_UseJSData(): Promise<void> {
        const self = this
        let returnValue = self._OnLoadComplete = (async () => {
            let msg = ""
            let recordResolver = new ESRecordResolver(self.dbContext,
                {
                    standardConditions: {
                        [esWB_Period_Hierarchy_Level]: {DELETED_IND: {neq: ['1']}},
                        [esWB_Product_Group_Relationship]: {DELETED_IND: {neq: ['1']}},
                        [esWB_Finished_Product_Relationship]: {DELETED_IND: {neq: ['1']}},
                    }
                })

            self._loadMessageProvider.clearMessages()

            // Get the Hierarchy Record
            await recordResolver.findAllWithRelationships(esWB_Product_Hierarchy, [self._wbPrdHId])
            let aPrdHRow: WB_ProductHierarchyRow = <any>recordResolver.get(esWB_Product_Hierarchy, self._wbPrdHId)

            if (_.isEmpty(aPrdHRow)) {
                msg = `Unknown Product Hierarchy ${self._wbPrdHId}`
                self.LoadMessageProvider.addMessage(
                    {code: "MissingPrdH", type: MessageType.Error, title: msg, description: msg})
                return
            }

            self._PrdHId = aPrdHRow.cif.WB_PRD_HIER_ID
            self._PrdHName = aPrdHRow.cif.WB_PRD_HIER_NAME
            if (_.isEmpty(self._PrdHName))
                self._PrdHName = aPrdHRow.cif.WB_PRD_HIER_ID

            // Populate Levels from MetaData
            _.forEach(_.filter(aPrdHRow.WB_ProductHierarchyLevelRows, aRow => aRow.cif.DELETED_IND != '1'), (aLevelRow) => {
                if (aLevelRow.cif.LEVEL_ID in self.dctHierarchyLevels) {
                    msg = `Product Hierarchy Level (${aLevelRow.cif.LEVEL_ID}) duplicated`
                    self.LoadMessageProvider.addMessage(
                        {code: "DupPrdHLvl", type: MessageType.Warning, title: msg, description: msg})
                    return // Next Level
                }

                self.dctHierarchyLevels[aLevelRow.cif.LEVEL_ID] = {
                    LevelId: aLevelRow.cif.LEVEL_ID,
                    LevelName: aLevelRow.cif.LEVEL_NAME,
                    LevelDepth: aLevelRow.cif.LEVEL_DEPTH
                }

            })

            // Read in the Relationship and Nodes
            let arrRelRows: WB_ProductGroupRelationshipRow[] = <any>
                await recordResolver.queryAllWithRelationships(esWB_Product_Group_Relationship, {
                    where: {
                        WB_PRDH_ID: this._wbPrdHId,
                        DELETED_IND: {'nin': ['1']}
                    }
                })

            //-- Sort by Child, StartDt, Key to make the results repeatable
            arrRelRows = _.sortBy(arrRelRows, ['WB_PRDGRP_ID', 'EFFECTIVE_START_DT', 'WB_PRDGRPR_ID'])

            _.forEach(arrRelRows, (aRow) => {

                //--  Check if relation in valid period
                if (!DateUtils.RangeDatesOverlap(self._effectiveStartDt, self._effectiveEndDt, aRow.EffectiveStartDt, aRow.EffectiveEndDt))
                    return

                //-- Only look at rows that have valid parents and children
                if (_.isEmpty(aRow.WB_ProductGroupRowParent) || _.isEmpty(aRow.WB_ProductGroupRowChild))
                    return

                let parent = self.addNode_PG(aRow.WB_ProductGroupRowParent)
                let child = self.addNode_PG(aRow.WB_ProductGroupRowChild)
                self.addHierarchyChildParentRel(child, parent)

            })

            //-- Leaving out for performance reasons

            if (!self._bExcludeProducts) {
                self.dctHierarchyLevels['Level99999'] = {
                    LevelId: 'Level99999',
                    LevelName: 'Product',
                    LevelDepth: 99999
                }
                let arrRelPrdRows: WB_FinishedProductRelationshipRow[] = <any>
                    await recordResolver.queryAllWithRelationships(esWB_Finished_Product_Relationship, {
                        where: {
                            WB_PRDH_ID: this._wbPrdHId,
                            DELETED_IND: {'nin': ['1']}
                        }
                    })

                arrRelPrdRows = _.sortBy(arrRelPrdRows, ['WB_PROD_ID', 'EFFECTIVE_START_DT', 'WB_PRODR_ID'])

                _.forEach(arrRelPrdRows, (aRow: WB_FinishedProductRelationshipRow) => {

                    //--  Check if relation in valid period
                    if (!DateUtils.RangeDatesOverlap(self._effectiveStartDt, self._effectiveEndDt, aRow.EffectiveStartDt, aRow.EffectiveEndDt))
                        return

                    //-- Only look at rows that have valid parents and children
                    if (_.isEmpty(aRow.WB_FinishedProductRow) || _.isEmpty(aRow.WB_ProductGroupRow))
                        return

                    let parent = self.addNode_PG(aRow.WB_ProductGroupRow)
                    let child = self.addNode_Product(aRow.WB_FinishedProductRow)
                    self.addHierarchyChildParentRel(child, parent)

                })
            }

            // Determine Top Level Nodes, nodes without Parents.
            _.forEach(this.dctNodes, (aNode, nodeId) => {
                if (!('ParentId' in aNode))
                    this.arrTopLevelNodes.push(aNode)
            })

            if(!self._bUseLegacyLevel)
                self.resetNodeLevelIdUsingDepth()

            // Find Levels In Nodes but not in Hierarchy
            _.forEach(this.dctNodesByLevel, (arrNodes, levelId) => {
                if (levelId in this.dctHierarchyLevels)
                    return

                // Create missing Level
                let newLevel: IHierarchyLevel = {
                    LevelId: levelId,
                    LevelName: levelId,
                    LevelDepth: 0
                }

                this.dctHierarchyLevels[levelId] = newLevel
            })


            self._bLoaded = true
        })();

        return returnValue
    }

    private get OnLoadComplete_UseHierarchyBuilder(): Promise<void> {
        const self = this
        let returnValue = self._OnLoadComplete = (async () => {
            let msg = ""

            self._loadMessageProvider.clearMessages()

            let hb = new HierarchyBuilderService()
            let hbResponse = await hb.getHierarchyComponents(DimensionType.Product, self._wbPrdHId, {
                EffectiveStartDt: self._effectiveStartDt, EffectiveEndDt: self._effectiveEndDt, bExcludeProducts: this._bExcludeProducts
            })

            // Get the Hierarchy Record

            self._PrdHId = hbResponse.HierarchyIdUI
            self._PrdHName = hbResponse.HierarchyName
            if (_.isEmpty(self._PrdHName))
                self._PrdHName = hbResponse.HierarchyName

            // Populate Levels from MetaData
            _.forEach(hbResponse.Levels, (aLevel) => {
                if (aLevel.LevelId in self.dctHierarchyLevels) {
                    msg = `Product Hierarchy Level (${aLevel.LevelId}) duplicated`
                    self.LoadMessageProvider.addMessage(
                        {code: "DupPrdHLvl", type: MessageType.Warning, title: msg, description: msg})
                    return // Next Level
                }

                self.dctHierarchyLevels[aLevel.LevelId] = aLevel

            })

            // Add Nodes to main dictionary and Level Array
            _.forEach(hbResponse.Nodes, (aNode) => {

                self.dctNodes[aNode.NodeId] = aNode
                if(self._bUseLegacyLevel) {
                    if (aNode.NodeLevel in this.dctNodesByLevel)
                        this.dctNodesByLevel[aNode.NodeLevel].push(aNode)
                    else
                        this.dctNodesByLevel[aNode.NodeLevel] = [aNode]
                }

                if ('ParentId' in aNode) {
                    if (aNode.ParentId in this.dctNodeChildrenByParentId)
                        this.dctNodeChildrenByParentId[aNode.ParentId].push(aNode)
                    else
                        this.dctNodeChildrenByParentId[aNode.ParentId] = [aNode]
                }

            })


            // Do a second pass to find the parents
            _.forEach(hbResponse.Nodes, (aNode) => {
                if (!('ParentId' in aNode))
                    return

                let parentId = aNode.ParentId
                let parent = this.dctNodes[parentId]
                if (parent)
                    this.dctNodeParentByChildId[aNode.NodeId] = parent

            });

            // Determine Top Level Nodes, nodes without Parents.
            _.forEach(this.dctNodes, (aNode, nodeId) => {
                if (!('ParentId' in aNode))
                    this.arrTopLevelNodes.push(aNode)
            })

            if(!self._bUseLegacyLevel)
                self.resetNodeLevelIdUsingDepth()

            // Find Levels In Nodes but not in Hierarchy
            _.forEach(this.dctNodesByLevel, (arrNodes, levelId) => {
                if (levelId in this.dctHierarchyLevels)
                    return

                // Create missing Level
                let newLevel: IHierarchyLevel = {
                    LevelId: levelId,
                    LevelName: levelId,
                    LevelDepth: 0
                }

                this.dctHierarchyLevels[levelId] = newLevel
            })


            self._bLoaded = true
        })();

        return returnValue
    }

    private resetNodeLevelIdUsingDepth(){
        const self = this

        self.dctNodesByLevel = {}

        // Reset Levels
        _.forEach(_.values(self.dctNodes), (n) => {
            // @ts-expect-error "Readonly"
            n["NodeLevel"] = ""
        })

        let dctLevelsByDepth = _.keyBy(self.dctHierarchyLevels, l => l.LevelDepth)

        function setNodeLevelByDepth(aNode: IHierarchyNode, depth: number){
            if(depth in dctLevelsByDepth){
                let lvl = dctLevelsByDepth[depth]
                // @ts-expect-error "Readonly
                aNode["NodeLevel"] = lvl.LevelId
            } else {
                // @ts-expect-error "Readonly"
                aNode["NodeLevel"] = '' + depth
            }
            // Set children depth recursively
            for(let child of (self.dctNodeChildrenByParentId[aNode.NodeId] || [])){
                setNodeLevelByDepth(child, depth + 1)
            }
        };

        _.forEach(self.arrTopLevelNodes, (node) => {
            setNodeLevelByDepth(node, 1)
        })

        _.forEach(_.values(self.dctNodes), (n) => {
            if(n.NodeLevel in self.dctNodesByLevel)
                self.dctNodesByLevel[n.NodeLevel].push(n)
            else
                self.dctNodesByLevel[n.NodeLevel] = [n]
        });
    }

    private addNode_PG(aPrdGrpRow: WB_ProductGroupRow): IHierarchyNode {
        //--
        if (_.isEmpty(aPrdGrpRow))
            return undefined

        let wbPrdGrpId = aPrdGrpRow.cif.WB_PRDGRP_ID + ''
        let levelId = aPrdGrpRow.cif.LEVEL_ID

        if (wbPrdGrpId in this.dctNodes)
            return this.dctNodes[wbPrdGrpId]

        let newNode: IHierarchyNode = {
            NodeId: wbPrdGrpId,
            NodeIdUI: aPrdGrpRow.cif.PRODUCT_GROUP_ID,
            NodeName: aPrdGrpRow.cif.PRODUCT_GROUP_NAME,
            NodeLevel: aPrdGrpRow.cif.LEVEL_ID,
            NodeOrder: this.bSortById ? aPrdGrpRow.cif.PRODUCT_GROUP_ID : aPrdGrpRow.cif.PRODUCT_GROUP_NAME,
            NodeShortName: aPrdGrpRow.cif.PRODUCT_GROUP_NAME
        }

        this.dctNodes[wbPrdGrpId] = newNode

        if (levelId in this.dctNodesByLevel)
            this.dctNodesByLevel[levelId].push(newNode)
        else
            this.dctNodesByLevel[levelId] = [newNode]

        return this.dctNodes[wbPrdGrpId]
    }

    private addNode_Product(aProductRow: WB_FinishedProductRow): IHierarchyNode {
        //--
        if (_.isEmpty(aProductRow))
            return undefined

        let wbProductId = aProductRow.cif.WB_PROD_ID + ''
        let levelId = 'Level99999'

        if (wbProductId in this.dctNodes)
            return this.dctNodes[wbProductId]

        let newNode: IHierarchyNode = {
            NodeId: wbProductId,
            NodeIdUI: aProductRow.cif.PRODUCT_ID,
            NodeName: aProductRow.cif.PRODUCT_NAME,
            NodeLevel: 'Level99999',
            NodeOrder: this.bSortById ? aProductRow.cif.PRODUCT_ID : aProductRow.cif.PRODUCT_NAME,
            NodeShortName: aProductRow.cif.PRODUCT_NAME
        }

        this.dctNodes[wbProductId] = newNode

        if (levelId in this.dctNodesByLevel)
            this.dctNodesByLevel[levelId].push(newNode)
        else
            this.dctNodesByLevel[levelId] = [newNode]

        return this.dctNodes[wbProductId]
    }

    private addHierarchyChildParentRel(aChild: IHierarchyNode, aParent: IHierarchyNode): void {

        let nodeIdChild = aChild.NodeId
        let nodeIdParent = aParent.NodeId

        if (nodeIdChild in this.dctNodeParentByChildId) {
            let msg = `Child ${aChild.NodeName} (${aChild.NodeId}) has duplicate Parent ${aParent?.NodeName} (${aParent?.NodeIdUI})`
            this._loadMessageProvider.addMessage({
                code: "DuplicateParent",
                type: MessageType.Warning,
                title: msg,
                description: msg
            })
            return
        }

        this.dctNodeParentByChildId[nodeIdChild] = aParent
        aChild.ParentId = aParent.NodeId

        if (nodeIdParent in this.dctNodeChildrenByParentId)
            this.dctNodeChildrenByParentId[nodeIdParent].push(aChild)
        else
            this.dctNodeChildrenByParentId[nodeIdParent] = [aChild]
    }

    getLevels(): IHierarchyLevel[] {
        return _.orderBy(_.values(this.dctHierarchyLevels), 'LevelId')
    }

    getTopLevelNodes(): IHierarchyNode[] {
        return this.arrTopLevelNodes;
    }

    getNode(NodeId: string): IHierarchyNode {

        if (NodeId in this.dctNodes)
            return this.dctNodes[NodeId]

        return undefined;
    }

    getNodes(): IHierarchyNode[] {
        return _.values(this.dctNodes)
    }

    getNodesAtLevel(HierarchyLevel: string): IHierarchyNode[] {
        if (HierarchyLevel in this.dctNodesByLevel)
            return this.dctNodesByLevel[HierarchyLevel]

        return []
    }

    getChildren(nodes: IHierarchyNode[] | IHierarchyNode | string[] | string, levelId?: string): IHierarchyNode[] {
        let arrNodeIds = this.convertToKeyArray(nodes)
        let bCheckLevel = !_.isEmpty(levelId)
        let returnValue: IHierarchyNode[] = []

        let arrRecurseOnChildren: IHierarchyNode[] = []
        _.forEach(arrNodeIds, (aNodeId) => {
            if (aNodeId in this.dctNodeChildrenByParentId) {
                let arrChildren = this.dctNodeChildrenByParentId[aNodeId]
                _.forEach(arrChildren, (aChildNode) => {
                    if (bCheckLevel) {
                        if (levelId == aChildNode.NodeLevel)
                            returnValue.push(aChildNode)
                        else
                            arrRecurseOnChildren.push(aChildNode)
                    } else {
                        returnValue.push(aChildNode)
                    }
                })
            }
        })

        // Recurse
        if (bCheckLevel && !_.isEmpty(arrRecurseOnChildren)) {
            let moreChildren = this.getChildren(arrRecurseOnChildren, levelId)
            _.forEach(moreChildren, (a) => returnValue.push(a))
        }

        return returnValue;
    }

    getParents(nodes: IHierarchyNode[] | IHierarchyNode | string[] | string, levelId?: string): { [NodeId: string]: IHierarchyNode } {
        let arrNodeIds = this.convertToKeyArray(nodes)

        let bCheckLevel = !_.isEmpty(levelId)
        let returnValue: { [NodeId: string]: IHierarchyNode } = {}


        let heightLimit = 20
        _.forEach(arrNodeIds, (aNodeId) => {
            let depth = 0
            let parent = this.dctNodeParentByChildId[aNodeId]
            if (bCheckLevel) {
                while (parent && (parent?.NodeLevel != levelId) && (depth < heightLimit)) {
                    parent = this.dctNodeParentByChildId[parent.NodeId]
                    depth += 1
                }
                if (depth == heightLimit)
                    parent = undefined
            }

            returnValue[aNodeId] = parent

        })

        return returnValue;
    }


    private convertToKeyArray(nodes: IHierarchyNode[] | IHierarchyNode | string[] | string): string[] {
        let returnValue: string[] = []

        if (_.isEmpty(nodes) && !_.isNumber(nodes))
            return returnValue

        let keyList: any[]
        if (_.isArray(nodes))
            keyList = nodes
        else
            keyList = [nodes]

        _.forEach(keyList, (aKeyItem) => {
            if (_.isString(aKeyItem)) {
                returnValue.push(aKeyItem)
                return
            }

            if (_.isNumber(aKeyItem))
                returnValue.push(aKeyItem + '')

            if (_.isEmpty(aKeyItem))
                return


            if ('NodeId' in aKeyItem)
                returnValue.push(aKeyItem.NodeId)
        })

        return returnValue
    }


}
